// client/src/pages/Register.js
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import colleges from "../data/colleges";

const BASE = process.env.REACT_APP_API_URL;
const OTP_LENGTH = 6;

/* ---------------- OTP INPUT BOXES ---------------- */
function OtpInputs({ value, setValue, disabled }) {
  const inputsRef = useRef([]);

  useEffect(() => {
    const idx = value.length < OTP_LENGTH ? value.length : OTP_LENGTH - 1;
    const el = inputsRef.current[idx];
    if (el && !disabled) el.focus();
  }, [value, disabled]);

  const onChange = (e, idx) => {
    if (disabled) return;
    const v = e.target.value.replace(/\D/g, "").slice(0, 1);
    let arr = value.split("");
    arr[idx] = v || "";
    setValue(arr.join("").slice(0, OTP_LENGTH));
    if (v) inputsRef.current[idx + 1]?.focus();
  };

  const onKeyDown = (e, idx) => {
    if (disabled) return;
    if (e.key === "Backspace") {
      let arr = value.split("");
      arr[idx] = "";
      setValue(arr.join(""));
      if (idx > 0) inputsRef.current[idx - 1]?.focus();
    }
    if (e.key === "ArrowLeft") inputsRef.current[idx - 1]?.focus();
    if (e.key === "ArrowRight") inputsRef.current[idx + 1]?.focus();
  };

  return (
    <div className="flex gap-2 justify-center mt-2">
      {Array.from({ length: OTP_LENGTH }).map((_, i) => (
        <input
          key={i}
          ref={(r) => (inputsRef.current[i] = r)}
          value={value[i] || ""}
          onChange={(e) => onChange(e, i)}
          onKeyDown={(e) => onKeyDown(e, i)}
          inputMode="numeric"
          maxLength={1}
          className="w-12 h-12 text-center rounded-lg border bg-white dark:bg-slate-700 dark:text-white"
          disabled={disabled}
        />
      ))}
    </div>
  );
}

/* ---------------- MAIN REGISTER COMPONENT ---------------- */
export default function Register() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    college: null,
    year: "",
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [filtered, setFiltered] = useState(colleges);

  /* ---------------- EMAIL OTP STATES ---------------- */
  const [emailOtp, setEmailOtp] = useState("");
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailOtpLoading, setEmailOtpLoading] = useState(false);
  const [emailResendTimer, setEmailResendTimer] = useState(0);

  const [loading, setLoading] = useState(false);

  /* ---------------- OUTSIDE CLICK CLOSE DROPDOWN ---------------- */
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest(".college-dropdown")) {
        setShowDropdown(false);
      }
    };
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, []);

  /* ---------------- FILTER COLLEGES ---------------- */
  useEffect(() => {
    const q = search.trim().toLowerCase();
    if (!q) return setFiltered(colleges);

    setFiltered(
      colleges.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.city.toLowerCase().includes(q) ||
          c.state.toLowerCase().includes(q) ||
          c.country.toLowerCase().includes(q)
      )
    );
  }, [search]);

  /* ---------------- TIMER FOR RESEND ---------------- */
  useEffect(() => {
    if (emailResendTimer > 0) {
      const t = setTimeout(() => setEmailResendTimer((s) => s - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [emailResendTimer]);

  /* ---------------- SEND EMAIL OTP ---------------- */
  const sendEmailOtp = async () => {
    if (!form.email) return alert("Enter a valid email");

    setEmailOtpLoading(true);
    try {
      const res = await fetch(`${BASE}/api/otp/send-email-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email }),
      });

      const d = await res.json();
      if (!res.ok) throw new Error(d.error);

      setEmailOtpSent(true);
      setEmailOtp("");
      setEmailVerified(false);
      setEmailResendTimer(60);

      alert("Email OTP sent!");
    } catch (err) {
      alert(err.message);
    }
    setEmailOtpLoading(false);
  };

  /* ---------------- VERIFY EMAIL OTP ---------------- */
  const verifyEmailOtp = async () => {
    if (emailOtp.length !== 6) return alert("Enter full OTP");

    setEmailOtpLoading(true);
    try {
      const res = await fetch(`${BASE}/api/otp/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email, otp: emailOtp }),
      });

      const d = await res.json();
      if (!res.ok) throw new Error(d.error);

      setEmailVerified(true);
      alert("Email verified!");
    } catch (err) {
      alert(err.message);
    }
    setEmailOtpLoading(false);
  };

  /* ---------------- REGISTER ---------------- */
  const handleRegister = async (e) => {
    e.preventDefault();

    if (!emailVerified) return alert("Please verify email first");

    setLoading(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        password: form.password,
        year: form.year,
        college: form.college?.name || form.college || "",
      };

      const res = await fetch(`${BASE}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const d = await res.json();
      if (!res.ok) throw new Error(d.error);

      alert("Registration successful!");
      navigate("/login");
    } catch (err) {
      alert(err.message);
    }
    setLoading(false);
  };

  const displayCollege = (c) =>
    c ? `${c.name}${c.city ? ` — ${c.city}` : ""}` : "";

  /* ---------------- UI START ---------------- */
  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900 flex justify-center items-center py-8 px-4">
      <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-xl w-full max-w-xl">

        <h2 className="text-3xl font-bold text-center text-indigo-600 dark:text-indigo-400 mb-6">
          Create Account
        </h2>

        <form onSubmit={handleRegister} className="space-y-5">

          {/* NAME */}
          <input
            placeholder="Full Name"
            required
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            className="w-full p-3 rounded-lg border dark:bg-slate-700 dark:text-white"
          />

          {/* EMAIL */}
          <input
            type="email"
            placeholder="Email (required)"
            required
            value={form.email}
            onChange={(e) => {
              update("email", e.target.value);
              setEmailVerified(false);
              setEmailOtpSent(false);
              setEmailOtp("");
            }}
            className="w-full p-3 rounded-lg border dark:bg-slate-700 dark:text-white"
          />

          {/* PHONE (optional) */}
          <input
            placeholder="Phone (optional)"
            value={form.phone}
            onChange={(e) =>
              update("phone", e.target.value.replace(/\D/g, "").slice(0, 10))
            }
            className="w-full p-3 rounded-lg border dark:bg-slate-700 dark:text-white"
          />

          {/* PASSWORD + YEAR */}
          <div className="grid grid-cols-2 gap-3">
            <input
              type="password"
              placeholder="Password"
              required
              value={form.password}
              onChange={(e) => update("password", e.target.value)}
              className="p-3 rounded-lg border dark:bg-slate-700 dark:text-white"
            />

            <select
              value={form.year}
              onChange={(e) => update("year", e.target.value)}
              required
              className="p-3 rounded-lg border dark:bg-slate-700 dark:text-white"
            >
              <option value="">Select Year</option>
              <option>1st Year</option>
              <option>2nd Year</option>
              <option>3rd Year</option>
              <option>4th Year</option>
              <option>Other</option>
            </select>
          </div>

          {/* COLLEGE SEARCH */}
          <div className="relative college-dropdown">
            <input
              placeholder="Search your college"
              value={
                search ||
                (form.college
                  ? typeof form.college === "object"
                    ? form.college.name
                    : form.college
                  : "")
              }
              onChange={(e) => {
                setSearch(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              className="w-full p-3 rounded-lg border dark:bg-slate-700 dark:text-white"
            />

            {showDropdown && (
              <div className="absolute bg-white dark:bg-slate-800 border rounded-lg max-h-56 overflow-auto w-full z-50">
                {filtered.map((c, i) => (
                  <div
                    key={i}
                    onClick={() => {
                      update("college", c);
                      setSearch(c.name);
                      setShowDropdown(false);
                    }}
                    className="p-3 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700"
                  >
                    <div className="font-medium dark:text-white">{c.name}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-300">
                      {c.city}, {c.state} • {c.country}
                    </div>
                  </div>
                ))}

                <div
                  onClick={() => {
                    const custom = prompt("Enter your college name:");
                    if (custom) {
                      update("college", custom);
                      setSearch(custom);
                    }
                    setShowDropdown(false);
                  }}
                  className="p-3 text-indigo-600 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  + Other (Add your college)
                </div>
              </div>
            )}
          </div>

          {/* SELECTED COLLEGE */}
          {form.college && (
            <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg text-sm">
              Selected: <strong>{displayCollege(form.college)}</strong>
              <button
                type="button"
                onClick={() => {
                  update("college", null);
                  setSearch("");
                }}
                className="ml-3 text-red-500 underline"
              >
                Clear
              </button>
            </div>
          )}

          {/* EMAIL OTP */}
          <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
            <div className="flex justify-between items-center">
              <div className="text-sm dark:text-white">{form.email || "—"}</div>

              {!emailOtpSent && !emailVerified && (
                <button
                  type="button"
                  disabled={emailOtpLoading}
                  onClick={sendEmailOtp}
                  className="px-3 py-1 bg-indigo-600 text-white rounded"
                >
                  {emailOtpLoading ? "Sending…" : "Send OTP"}
                </button>
              )}

              {emailOtpSent && !emailVerified && (
                <button
                  type="button"
                  disabled={emailOtp.length !== 6}
                  onClick={verifyEmailOtp}
                  className="px-3 py-1 bg-green-600 text-white rounded"
                >
                  Verify
                </button>
              )}

              {emailVerified && (
                <div className="px-3 py-1 bg-green-50 text-green-700 rounded">
                  Verified ✓
                </div>
              )}
            </div>

            {emailOtpSent && !emailVerified && (
              <>
                <OtpInputs value={emailOtp} setValue={setEmailOtp} />
                <div className="mt-2 text-center text-xs text-slate-500 dark:text-slate-400">
                  {emailResendTimer > 0 ? (
                    <>Resend in {emailResendTimer}s</>
                  ) : (
                    <button
                      type="button"
                      onClick={sendEmailOtp}
                      className="underline text-indigo-600 dark:text-indigo-400"
                    >
                      Resend OTP
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          {/* SUBMIT */}
          <button
            type="submit"
            disabled={loading || !emailVerified}
            className="w-full py-3 rounded-lg text-white text-lg font-semibold 
            bg-gradient-to-r from-indigo-600 to-purple-600 hover:scale-[1.02] transition"
          >
            {loading ? "Creating Account…" : "Sign Up"}
          </button>
        </form>

        <p className="mt-4 text-center text-slate-700 dark:text-slate-300">
          Already have an account?
          <span
            onClick={() => navigate("/login")}
            className="text-indigo-600 dark:text-indigo-400 underline cursor-pointer ml-1"
          >
            Login
          </span>
        </p>
      </div>
    </div>
  );
}
